/*
* Copyright  2025-2026, Airspan
* All Rights Reserved
*
* This code is unpublished, confidential and proprietary to Airspan and may only be used in accordance with the appropriate
* license agreement. All other copying, modification, distribution, or
* reverse engineering are strictly prohibited.
*/

import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "../../lib/utils";

export interface SheetProps {
    open: boolean;
    onClose: () => void;
    title?: string;
    subtitle?: string;
    children?: React.ReactNode;
    footer?: React.ReactNode;
    side?: "right" | "left";
    width?: string;
    useCardStyle?: boolean;
    showCloseButton?: boolean;
}

export const Sheet: React.FC<SheetProps> = ({
    open,
    onClose,
    title,
    subtitle,
    children,
    footer,
    side = "right",
    width = "512px",
    useCardStyle = false,
    showCloseButton = true,
}) => {
    return (
        <DialogPrimitive.Root open={open} onOpenChange={(val) => { if (!val) onClose(); }} modal={false}>
            <DialogPrimitive.Portal>
                {/* Overlay */}
                <div
                    onClick={onClose}
                    className={cn(
                        "fixed inset-0 z-[1300] bg-black/30 backdrop-blur-[1px]",
                    )}
                />
                {/* Content */}
                <DialogPrimitive.Content
                    aria-describedby={undefined}
                    onInteractOutside={(e) => e.preventDefault()}
                    onPointerDownOutside={(e) => e.preventDefault()}
                    className={cn(
                        "fixed z-[1301] flex flex-col bg-background text-foreground shadow-2xl outline-none",
                        "inset-y-0",
                        side === "right"
                            ? "right-0 border-l border-border rounded-l-[10px]"
                            : "left-0 border-r border-border rounded-r-[10px]",
                        "data-[state=open]:animate-in data-[state=closed]:animate-out",
                        "data-[state=closed]:duration-220 data-[state=open]:duration-380",
                        side === "right"
                            ? "data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right"
                            : "data-[state=open]:slide-in-from-left data-[state=closed]:slide-out-to-left"
                    )}
                    style={{
                        width,
                        maxWidth: "100vw",
                    }}
                >
                    {/* Close button - positioned relative to content panel like Vite */}
                    {showCloseButton && (
                        <DialogPrimitive.Close
                            className="absolute right-3 top-3 z-10 rounded-sm bg-transparent text-foreground opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none"
                            aria-label="Close"
                        >
                            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18" />
                                <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                            <span className="sr-only">Close</span>
                        </DialogPrimitive.Close>
                    )}

                    {/* Header */}
                    {(title || subtitle) && (
                        <div className="border-b border-border px-6 py-4 pr-12 shrink-0">
                            <div className="flex flex-col gap-1.5 text-center sm:text-left">
                                {title && (
                                    <DialogPrimitive.Title asChild>
                                        <h2 className="m-0 text-lg font-semibold text-foreground normal-case">
                                            {title}
                                        </h2>
                                    </DialogPrimitive.Title>
                                )}
                                {subtitle && (
                                    <p className="m-0 text-sm text-muted-foreground normal-case">
                                        {subtitle}
                                    </p>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Body */}
                    <div className="flex-1 overflow-y-auto px-5 py-4">
                        {useCardStyle ? (
                            <div className="rounded-md border border-border bg-card p-5 text-card-foreground shadow-sm">
                                {children}
                            </div>
                        ) : children}
                    </div>

                    {/* Footer */}
                    {footer && (
                        <div className="border-t border-border px-4 py-4">
                            <div className="flex w-full items-center justify-end gap-2">
                                {footer}
                            </div>
                        </div>
                    )}
                </DialogPrimitive.Content>
            </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
    );
};